import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { notificationText } from "src/notifications/notificationText";
import type { NotificationItem } from "src/notifications/useNotifications";
import { formatRelativeTime } from "src/utils/relativeTime";

/** Figma 2:3968's unread marker: a 10px dot left of the line. */
const DOT_SIZE = 10;
const CARD_MESSAGE_MIN_WIDTH = 220;
/** "unread" for screen readers, which do not see the dot. */
const VISUALLY_HIDDEN = {
	position: "absolute",
	width: "1px",
	height: "1px",
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap"
} as const;

const UnreadDot: FC<{ unread: boolean }> = ({ unread }) => (
	<Box
		aria-hidden
		sx={{
			flexShrink: 0,
			width: DOT_SIZE,
			height: DOT_SIZE,
			mt: 0.75,
			borderRadius: "50%",
			bgcolor: unread ? "primary.main" : "transparent"
		}}
	/>
);

/** "**Bo Gavey published a new model** — Silicon Valley ⇄ AWS us-west-2. 2 hours ago." */
const NotificationMessage: FC<{ notification: NotificationItem }> = ({ notification }) => {
	const { t, i18n } = useTranslation();
	const { headline, values, detail } = notificationText(notification);
	const actor = values.actor ?? t("notifications.someone");
	const solution = values.solution ?? t("notifications.aSolution");
	return (
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Typography sx={{ overflowWrap: "anywhere" }}>
				<Box component="strong" sx={{ fontWeight: 600 }}>
					{t(`notifications.headline.${headline}`, { actor, solution })}
				</Box>
				{detail && ` — ${detail}`}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				{formatRelativeTime(notification.occurredAt, i18n.language)}
				{notification.count > 1 && ` · ${t("notifications.edits", { count: notification.count })}`}
				{!notification.readAt && <Box component="span" sx={VISUALLY_HIDDEN}>{` · ${t("notifications.unread")}`}</Box>}
			</Typography>
		</Box>
	);
};

interface NotificationRowProps {
	notification: NotificationItem;
	onOpen: (notification: NotificationItem) => void;
	/**
	 * `card`: Figma 2:3968's outlined row with a Review button (Launchpad, Activity). `compact`: the
	 * whole row is the button (the bell's list).
	 */
	variant?: "card" | "compact";
}

export const NotificationRow: FC<NotificationRowProps> = ({ notification, onOpen, variant = "card" }) => {
	const { t } = useTranslation();
	const handleOpen = useCallback(() => onOpen(notification), [notification, onOpen]);
	const unread = !notification.readAt;

	if (variant === "compact") {
		return (
			// The bell's list is a `ul`: each button sits in an `li` so the items and their count are announced.
			<ListItem disablePadding>
				<ListItemButton onClick={handleOpen} sx={{ gap: 1.5, alignItems: "flex-start", borderRadius: 2, py: 1.25 }}>
					<UnreadDot unread={unread} />
					<NotificationMessage notification={notification} />
				</ListItemButton>
			</ListItem>
		);
	}

	return (
		<Card
			component="li"
			sx={{
				listStyle: "none",
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				gap: 2,
				px: { xs: 2, sm: 3 },
				py: 2
			}}>
			<Box sx={{ display: "flex", gap: 2, flex: `1 1 ${CARD_MESSAGE_MIN_WIDTH}px`, minWidth: 0 }}>
				<UnreadDot unread={unread} />
				<NotificationMessage notification={notification} />
			</Box>
			<Button variant="outlined" size="small" onClick={handleOpen} sx={{ whiteSpace: "nowrap" }}>
				{t("notifications.review")}
			</Button>
		</Card>
	);
};
